import { commitMutation, graphql } from "react-relay";
import { Environment } from "react-relay";
import { PayloadError } from "relay-runtime";

import {
  CreateLabeledBoxMutationInput,
  CreateLabeledBoxMutationResponse
} from "../__generated__/CreateLabeledBoxMutation.graphql";

import { FrameViewer_viewer } from "../__generated__/FrameViewer_viewer.graphql";

const mutation = graphql`
  mutation CreateLabeledBoxMutation($input: CreateLabeledBoxMutationInput!) {
    createLabeledBox(input: $input) {
      clientMutationId
      labeledBox {
        objectType
        id
        author {
          id
          username
        }
        number
        pallet {
          id
          number
        }
        frame {
          id
          labeledBoxesCount
          labeledBoxes {
            id
          }
          labeledPallets {
            boxes {
              id
            }
          }
        }
        shape {
          id
          faces {
            id
            side
            parentShape {
              id
              faces {
                id
              }
            }
            corners {
              id
              x
              y
              visibility
            }
          }
        }
      }
    }
  }
`;

let tempID = 0;
function createLabeledBox(
  viewer: FrameViewer_viewer,
  environment: Environment,
  input: CreateLabeledBoxMutationInput,
  onCompleted?: (
    response: CreateLabeledBoxMutationResponse | null,
    errors: Array<PayloadError> | null
  ) => void,
  onError?: (error: Error) => void
) {
  let mutationId: string = (tempID++).toString();
  const fullInput: CreateLabeledBoxMutationInput = {
    clientMutationId: mutationId,
    ...input
  };
  commitMutation(environment, {
    mutation,
    variables: {
      input: fullInput
    },
    configs: [],
    onCompleted,
    onError,
    optimisticUpdater: store => {
      let frame = store.get(input.frameId);
      if (!frame) {
        return;
      }
      const author = store.get(viewer.id);
      if (!author) {
        return;
      }
      let existingLabels = frame.getLinkedRecords("labeledBoxes");
      if (!existingLabels) {
        existingLabels = [];
      }
      const labelId = "client:createLabelLabel:" + mutationId;
      const shapeId = "client:createLabelShape:" + mutationId;
      const faceId = "client:createLabelFace:" + mutationId;
      const cornerId = "client:createLabelCorner:" + mutationId;

      let label = store.create(labelId, "LabeledBox");
      let shape = store.create(shapeId, "Cuboid");
      let face = store.create(faceId, "CuboidFace");
      let corner = store.create(cornerId, "CuboidCorner");

      label.setValue(labelId, "id");
      label.setValue("", "number");
      shape.setValue(shapeId, "id");
      face.setValue(faceId, "id");
      corner.setValue(cornerId, "id");

      // FIXME(malcolm): Support optimistic updates for pallets with more than one initial corner.
      corner.setValue(input.faces[0].newCorners[0].x, "x");
      corner.setValue(input.faces[0].newCorners[0].y, "y");
      corner.setValue(input.faces[0].newCorners[0].visibility, "visibility");
      corner.setValue(cornerId, "id");

      face.setLinkedRecords([corner], "corners");
      shape.setLinkedRecords([face], "faces");
      shape.setLinkedRecords([corner], "corners");
      face.setLinkedRecord(shape, "parentShape");
      label.setLinkedRecord(shape, "shape");
      label.setLinkedRecord(frame, "frame");
      label.setLinkedRecord(author, "author");
      frame.setLinkedRecords([label, ...existingLabels], "labeledBoxes");

      if (input.palletId) {
        const pallet = store.get(input.palletId);
        if (pallet) {
          label.setLinkedRecord(pallet, "pallet");
          const existingPalletBoxes = pallet.getLinkedRecords("boxes") || [];
          pallet.setLinkedRecords([...existingPalletBoxes, label], "boxes");
        }
      }
    }
  });
}

export default createLabeledBox;
